import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import React, { ReactElement, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { DownloadMenuProps } from "../../types/DownloadMenuProps";
import { Checkbox } from "primereact/checkbox";
import "./DownloadMenu.css";
import { SplitButton } from "primereact/splitbutton";
import { InputText } from "primereact/inputtext";
import { SHARE_ACTION } from "./Constants";
import { ActionsMenu } from "../../ActionsMenu/ActionsMenu";
import { validate } from "email-validator";

enum TermsAndConditionsMode {
    Download = 0,
    Share = 1
}

export const DownloadMenu = (props: DownloadMenuProps): ReactElement => {
    const { t } = useTranslation("common");
    const [displayTermsModal, setDisplayTermsModal] = useState(false);
    const [displayShareModal, setDisplayShareModal] = useState(false);
    const [isTermsAndConditionsAgreed, setIsTermsAndConditionsAgreed] =
        useState(false);
    const [showValidationMessage, setShowValidationMessage] = useState(false);
    const [emailValidationMessage, setEmailValidationMessage] =
        useState<boolean>(false);
    const [termsAndConditionsMode, setTermsAndConditionsMode] =
        useState<number>(TermsAndConditionsMode.Download);
    const [shareEmail, setShareEmail] = useState<string>("");

    const getLocaleValue = (key: string) => {
        return t(`inspectionReport.downloadMenu.${key}`);
    };

    const openModal = (download: string) => {
        setTermsAndConditionsMode(TermsAndConditionsMode.Download);
        setDisplayTermsModal(true);
        setIsTermsAndConditionsAgreed(false);
        setShowValidationMessage(false);
        props.setDownloadOption(download);
    };

    const openShareModal = () => {
        setTermsAndConditionsMode(TermsAndConditionsMode.Share);
        setShareEmail("");
        setEmailValidationMessage(false);
        setDisplayTermsModal(true);
        setIsTermsAndConditionsAgreed(false);
        setShowValidationMessage(false);
    };

    const agreeToTermsAndConditions = () => {
        const agreed = !isTermsAndConditionsAgreed;
        setIsTermsAndConditionsAgreed(agreed);
        setShowValidationMessage(!agreed);
    };

    const handleOnAgreeAndDownload = () => {
        if (isTermsAndConditionsAgreed) {
            props.onAgreeAndDownload();
            setDisplayTermsModal(false);
        } else {
            setShowValidationMessage(true);
        }
    };

    const handleOnAgreeAndShare = () => {
        if (isTermsAndConditionsAgreed) {
            setDisplayTermsModal(false);
            setDisplayShareModal(true);
        } else {
            setShowValidationMessage(true);
        }
    };

    const handleShareClick = () => {
        if (!shareEmail || !validate(shareEmail)) {
            setEmailValidationMessage(true);
            return;
        }

        setEmailValidationMessage(false);
        setDisplayShareModal(false);
        props.onShare && props.onShare(shareEmail);
    };

    const tAndCsFooter = (): ReactElement => {
        const submitLocaleRef =
            termsAndConditionsMode === TermsAndConditionsMode.Download
                ? "agreeAndDownload"
                : "shareContinue";
        const submitFunction =
            termsAndConditionsMode === TermsAndConditionsMode.Download
                ? handleOnAgreeAndDownload
                : handleOnAgreeAndShare;
        return (
            <div className="flex justify-content-center flex-wrap">
                <div className="flex align-items-center justify-content-center">
                    <Button
                        label={getLocaleValue("cancel")}
                        onClick={() => setDisplayTermsModal(false)}
                        className="p-button-text md:w-10rem"
                    />
                    <Button
                        label={getLocaleValue(submitLocaleRef)}
                        onClick={submitFunction}
                        autoFocus
                        className="agree-button"
                    />
                </div>
            </div>
        );
    };
    const shareFooter = (): ReactElement => {
        return (
            <div className="flex justify-content-center flex-wrap mt-4">
                <div className="flex align-items-center justify-content-center">
                    <Button
                        label={getLocaleValue("cancel")}
                        onClick={() => setDisplayShareModal(false)}
                        className="p-button-text w-10rem"
                    />
                    <Button
                        label={getLocaleValue("shareButton")}
                        onClick={handleShareClick}
                        autoFocus
                        className={"w-10rem"}
                    />
                </div>
            </div>
        );
    };

    const items = props.downloads
        .filter((download) => download.key !== SHARE_ACTION)
        .map((download) => {
            return {
                label: download.label,
                icon: "pi pi-download",
                command: () => openModal(download.key)
            };
        });

    const shareItem = props.downloads.find((item) => item.key === SHARE_ACTION);

    if (shareItem) {
        items.push({
            label: shareItem.label,
            icon: "pi pi-share-alt",
            command: () => openShareModal()
        });
    }

    if (props.showShareOptions) {
        items.push({
            label: getLocaleValue("sharingOptions"),
            icon: "pi pi-cog",
            command: () => {
                if (props.onClickShareOptions) {
                    props.onClickShareOptions();
                } else {
                    console.error("No onClickShareOptions provided");
                }
            }
        });
    }

    const renderInput = () => {
        if (props.previewMode) {
            if (!items || items.length == 0) {
                return (
                    <Button
                        label={getLocaleValue("viewPdf")}
                        onClick={props.onPeview}
                        className={"downloadMenuSplitButton"}
                    />
                );
            }

            return (
                <SplitButton
                    label={getLocaleValue("viewPdf")}
                    onClick={props.onPeview}
                    model={items}
                    className={"downloadMenuSplitButton"}
                />
            );
        }

        return <ActionsMenu menuItems={items} large />;
    };

    const termsAndConditionsText = () => {
        let dialogText: string[] = [];
        if (termsAndConditionsMode === TermsAndConditionsMode.Download) {
            dialogText = props.termsAndConditions ?? [];
        } else {
            dialogText = props.shareTermsAndConditions ?? [];
        }

        const tsAndCs = dialogText.map((text) => {
            return (
                <>
                    <p>{text}</p>
                    <br />
                </>
            );
        });

        tsAndCs.push(
            <>
                <Trans
                    ns="common"
                    i18nKey={"inspectionReport.downloadMenu.tsAndCs_contact"}
                    components={[
                        <a
                            className="tsAndCs-contact-us font-semibold"
                            key="contact-us"
                            href="https://www.idwalmarine.com/contact-us"
                            target="_blank"
                        />
                    ]}
                />
                <br />
                <br />
            </>
        );

        return tsAndCs;
    };

    return (
        <div>
            <Dialog
                header={props.modalHeader}
                headerClassName="font-semibold header-text"
                visible={displayTermsModal}
                className="dialog-medium"
                onHide={() => setDisplayTermsModal(false)}
                footer={tAndCsFooter}
            >
                {termsAndConditionsText()}

                <div className="col-12">
                    <Checkbox
                        className={showValidationMessage ? "p-invalid" : ""}
                        inputId="chkAgreeToTerms"
                        value="New York"
                        onChange={agreeToTermsAndConditions}
                        checked={isTermsAndConditionsAgreed}
                    ></Checkbox>
                    <label
                        htmlFor="chkAgreeToTerms"
                        className={`${
                            showValidationMessage ? "p-error" : ""
                        } p-checkbox-label ml-3`}
                    >
                        <Trans
                            ns="common"
                            i18nKey={
                                "inspectionReport.downloadMenu.tsAndCsAgreement"
                            }
                            components={[
                                <b></b>,
                                <a
                                    key="terms-and-conditions"
                                    className="tandc-link font-semibold"
                                    href={props.termsAndConditionsLink}
                                    target="_blank"
                                />
                            ]}
                        />
                    </label>
                    {showValidationMessage ? (
                        <p className="text-small mt-2 p-error">
                            {getLocaleValue("tsAndCsValidationMessage")}
                        </p>
                    ) : (
                        ""
                    )}
                </div>
            </Dialog>
            {renderInput()}
            <Dialog
                className="dialog-medium"
                header={getLocaleValue("shareTitle")}
                visible={displayShareModal}
                onHide={() => setDisplayShareModal(false)}
                footer={shareFooter}
            >
                <div className={"flex flex-column justify-content-evenly"}>
                    <p>{getLocaleValue("downloadSharePreamble")}</p>
                    <div className="grid mt-4">
                        <p className={"col-3"}>Email address:</p>
                        <InputText
                            value={shareEmail}
                            onChange={(e) => setShareEmail(e.target.value)}
                            className={"col-9"}
                        ></InputText>
                        {emailValidationMessage ? (
                            <p className="text-small mt-2 ml-3 p-error">
                                {getLocaleValue("shareEmailValidationMessage")}
                            </p>
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
            </Dialog>
        </div>
    );
};
